import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    comments: '',
    website_alt: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    return /^\+?\d{10,15}$/.test(cleanPhone);
  };

  const normalizePhone = (phone: string): string => {
    let clean = phone.replace(/[^\d+]/g, '');
    if (!clean.startsWith('+')) clean = '+' + clean;
    return clean;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (formData.website_alt.trim()) {
      setFormStatus('success');
      setFormData({ name: '', phone: '', company: '', comments: '', website_alt: '' });
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    if (!formData.name.trim()) {
      setErrorMessage('Пожалуйста, укажите ваше имя');
      return;
    }
    if (!formData.phone.trim()) {
      setErrorMessage('Пожалуйста, укажите ваш телефон');
      return;
    }
    if (!privacyAccepted) {
      setErrorMessage('Пожалуйста, подтвердите согласие на обработку персональных данных');
      return;
    }
    if (!validatePhone(formData.phone)) {
      setErrorMessage('Пожалуйста, введите корректный номер телефона (минимум 10 цифр)');
      return;
    }

    setFormStatus('submitting');
    setErrorMessage('');

    try {
      const normalizedPhone = normalizePhone(formData.phone);
      const baseUrl = 'https://businesstrainer.bitrix24.by/rest/1/yxbm0u72myjrxywc/crm.lead.add.json';
      const params = new URLSearchParams({
        'fields[NAME]': formData.name,
        'fields[TITLE]': `Лид с businesstraining - Энциклопедия ${formData.name}`,
        'fields[COMPANY_TITLE]': formData.company,
        'fields[PHONE][0][VALUE]': normalizedPhone,
        'fields[COMMENTS]': formData.comments,
        'params[REGISTER_SONET_EVENT]': 'Y',
        'ASSIGNED_BY_ID': '1',
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`, { method: 'GET' });

      if (response.ok) {
        const result = await response.json();
        if (result.result) {
          setFormStatus('success');
          setFormData({ name: '', phone: '', company: '', comments: '', website_alt: '' });
          setTimeout(() => setFormStatus('idle'), 5000);
        } else {
          throw new Error('Ошибка отправки');
        }
      } else {
        throw new Error('Ошибка отправки');
      }
    } catch {
      setFormStatus('error');
      setErrorMessage('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
      setTimeout(() => {
        setFormStatus('idle');
        setErrorMessage('');
      }, 5000);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-10 md:p-16 shadow-xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 md:mb-6">
              Поможем построить систему корпоративного обучения
            </h2>
            <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              Оставьте заявку — мы проведем диагностику, подберем оптимальную программу и поможем внедрить обучение с измеримым результатом.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <form
              id="encyclopedia_contact_form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-xl p-5 sm:p-8 shadow-lg"
            >
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="encyclo_name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Имя <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="encyclo_name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                    placeholder="Введите ваше имя"
                  />
                </div>

                <div>
                  <label htmlFor="encyclo_phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Телефон <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="encyclo_phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                    placeholder="+375 (XX) XXX-XX-XX"
                  />
                </div>

                <div>
                  <label htmlFor="encyclo_company" className="block text-sm font-semibold text-gray-900 mb-2">
                    Название компании
                  </label>
                  <input
                    type="text"
                    id="encyclo_company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base"
                    placeholder="Введите название компании"
                  />
                </div>

                <div>
                  <label htmlFor="encyclo_comments" className="block text-sm font-semibold text-gray-900 mb-2">
                    Запрос
                  </label>
                  <textarea
                    id="encyclo_comments"
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-all text-base resize-none"
                    placeholder="Опишите ваш запрос или вопрос (до 500 символов)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.comments.length}/500 символов</p>
                </div>

                {/* Honeypot */}
                <div className="website_alt_field">
                  <label htmlFor="encyclo_website_alt">Website</label>
                  <input
                    type="text"
                    id="encyclo_website_alt"
                    name="website_alt"
                    value={formData.website_alt}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {errorMessage && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {errorMessage}
                  </div>
                )}

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm text-gray-600 leading-snug group-hover:text-gray-900 transition-colors">
                    Я согласен(на) на{' '}
                    <a
                      href="https://sivko.by/load/privacy-policy.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 underline"
                    >
                      обработку персональных данных
                    </a>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting' || !privacyAccepted}
                  className="w-full bg-teal-600 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-lg text-sm sm:text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? 'Отправка...' : 'Отправить заявку'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}